// ============================================================================
// COST RECOMPUTE PROMPT
// ----------------------------------------------------------------------------
// Focused single-item cost-breakdown regeneration. Triggered when the user
// edits a scope item's inclusions via the QM Assistant - the existing cost
// estimate may no longer reflect the work, so we ask the AI to produce a
// fresh breakdown for THIS item only.
//
// Deliberately small prompt + small response: we don't need risk analysis,
// scope structuring, or any of the heavy context. Just costs.
// ============================================================================
export const COST_RECOMPUTE_PROMPT = `You are an expert Australian construction estimator. Recompute the cost breakdown for ONE scope item based on its updated inclusions.

{LOCALITY_BLOCK}

PROJECT CONTEXT:
{PROJECT_CONTEXT}

SCOPE ITEM:
Title: {TITLE}
Description: {DESCRIPTION}

UPDATED INCLUSIONS (this is the new scope of works for this item - your cost breakdown must reflect EXACTLY these inclusions, not the previous version):
{INCLUSIONS}

HOLISTIC SCOPE-ITEM PRICING (BINDING):
  - The parent scope item has ONE aggregate client price. The inclusion bullets are unpriced constituent work and cost drivers considered together; they are not separate quote lines.
  - Reason across the COMPLETE inclusion set plus the WORK METHOD when sizing labour, materials, equipment, subcontractors and other costs. Shared mobilisation, access, setup, cleanup, labour and materials must be counted once at the scope-item level, not repeated once per inclusion.
  - Do NOT create a one-to-one cost row for every inclusion and do NOT assign an amount, rate, margin or subtotal to an ordinary inclusion.
  - A deliberately marked client allowance may show a separate display figure upstream, but it is already contained within the parent item amount. Do not add that figure again or treat it as authority for the internal cost breakdown.
  - Return one coherent aggregate cost breakdown for the scope item. Its resource rows explain the whole delivery method; they do not need to mirror the inclusion count.
{PRICING_REFERENCE}

{STRATA_PLAN_HISTORY_BLOCK}

The STRATA_PLAN_HISTORY block (when present) lists prior won works at THIS strata plan from OTHER clients. For this PER-ITEM recompute it provides a cross-client price anchor: if THIS scope item's category matches a category that appears in the prior-works list, weight unitRate toward the historical avg-won range across clients.

{CLIENT_PRICING_PROFILE_BLOCK}

The CLIENT_PRICING_PROFILE block (when present) reports this Requester's historical posture — win rate + STRATEGY HINT (aggressive \ standard \ conservative \ unknown). For this PER-ITEM recompute:
  - AGGRESSIVE → bias THIS item's contingency_pct toward the lower end of the reasonable range (5-8%). Lean per-item cost protects the win-rate streak.
  - CONSERVATIVE → bias contingency_pct toward the upper end (12-15%). Risk margin protects against price-shopping.
  - STANDARD or UNKNOWN → keep contingency_pct at the default for this item type.
  - This block does NOT change unitRate or labour-hour figures (locality + PRICING REFERENCE govern those). It is the per-item posture-aware contingency dial.

CHOSEN APPROACH (project-level):
{CHOSEN_APPROACH}

WORK METHOD (this item):
{WORK_METHOD}

METHOD-INFORMED PRICING: If the WORK METHOD block above contains a sequence_of_works, trades_required, access_strategy, or programme_days, your cost breakdown MUST reflect those method inputs. Specifically:
  - Labour hours should approximate sequence_days × trade_count × 8h, not a generic single-trade estimate. A 3-day membrane application by a waterproofer + labourer pair is 48 burdened hours, not 16.
  - Equipment should reflect access_strategy. If access_strategy says "scaffold for 2 weeks", include scaffold hire for that duration. If access_strategy says "EWP half-day", do NOT price scaffold.
  - Trades in "trades_required" MUST appear as labour lines. Don't price a trade the method doesn't call for.
  - If the project-level CHOSEN APPROACH block sets a cost_band of "low", bias materials + labour toward the cheap end of the reasonable range. "High" - use quality products + experienced trades at upper-band rates.
  - If WORK METHOD is empty or says "no workMethod attached", fall through to legacy behaviour - cost from title + description + inclusions alone.

YOUR TASK:
Generate ONE fresh aggregate cost_estimate for this scope item that reflects the updated inclusions AND (when present) the method inputs above. Itemise the resource requirements across the complete delivery method: materials (qty x unit rate), labour (trade x hours x burdened hourly rate), equipment (scaffold, plant, skip bins), subcontractors (external trade packages or specialist contractors), and other costs (waste removal, certifications). Apply contingency (5-15% based on risk) and overhead (typically 8-12%).

Transparent cost stack: materials + labour + equipment + subcontractors + other MUST sum to direct_cost_total within $1. Do not bury subcontracted work inside labour. Do not put profit margin in any direct-cost category.

DO NOT include a margin or markup field. The quote price is computed by applying the project-level Profit Margin slider on top of total_cost in the Scope Builder, so you only need to produce the COST.

Return EXACTLY this JSON shape (no extra fields, no commentary):
{
  "materials": [{"description": "<material>", "qty": <num>, "unit": "<sqm|L|item|hr>", "unitRate": <num>, "total": <num>}],
  "labour": [{"trade": "<trade name>", "hours": <num>, "rate": <hourly burdened rate>, "workers": <num>, "total": <num>}],
  "equipment": [{"description": "<item>", "total": <num>}],
  "subcontractors": [{"trade": "<external trade or package>", "qty": <num>, "unit": "<hr|day|job|sqm|lm>", "rate": <num>, "total": <num>}],
  "other": [{"description": "<item>", "total": <num>}],
  "direct_cost_total": <number>,
  "contingency_pct": <number>,
  "overhead_pct": <number>,
  "total_cost": <number>
}

Use the market rates implied by the locality block above. Australian English spelling throughout - specialise, analyse, organise, colour, behaviour, labour, centre, metre, etc. (NEVER American -ize forms). Australian terminology. Return ONLY valid JSON, no markdown fences.`;

export const CONTACT_EXTRACTION_PROMPT = `You are extracting contact and project details from an Australian building/construction document (RFQ, email, work order, or quote request).

# INSTANCE OWNER CONTEXT

You are extracting contacts for an Australian building business. The business that operates this QuoteMaker install (THE INSTANCE OWNER) is described in the tenant block below. Any contact whose email domain matches the instance owner's email_domains, OR whose name matches staff_names, OR whose email matches staff_emails, is an INSTANCE OWNER STAFF MEMBER (NOT the client, NOT the site contact). Route these contacts to instanceOwnerStaff[] and leave clientName/clientEmail/siteContactName empty for them. Only after excluding instance-owner staff should you classify the remaining contacts into client and site contact slots.

<tenant>
\${TENANT_BLOCK}
</tenant>

CRITICAL RULES:
1. Return ONLY valid JSON - no markdown, no code blocks, no explanations
2. Use null ONLY when information is genuinely not present in the document
3. Extract ALL names, emails, phones, and addresses you can find - do NOT leave fields null if data exists anywhere in the document
4. SITE ADDRESS, STRATA PLAN, and ORDER/REFERENCE NUMBER are HIGH PRIORITY - search the entire document carefully for these
5. For Australian addresses: every address has a suburb + state code (NSW/VIC/QLD/SA/WA/TAS/NT/ACT) + 4-digit postcode
6. INSTANCE OWNER RULE: An email whose domain matches one of the instance owner's email_domains is NEVER the client and NEVER the site contact. Route to instanceOwnerStaff[]. Same rule applies when a contact's full name matches one in staff_names (case-insensitive).
7. REQUESTERS: list in requesters[] every person who requested or manages this request (strata manager, body corporate manager, assistant strata manager, the person who ordered or issued it), each with their title as printed. Give an email or phone ONLY if it is printed in the document for that person; never construct or guess an email address.

Return this exact JSON structure:
{
    "clientName": "<full name of the person who sent/ordered this work - e.g. the strata / body-corporate MANAGER named on the form. This is the client CONTACT person, not the company.>",
    "clientCompany": "<the CLIENT company that placed the order and is billed: the strata MANAGING AGENT / property manager / real estate agency / council. Use the company on the 'Bill To' line or the letterhead/logo (e.g. 'GK Strata Management', 'BCS Strata Management', 'Netstrata'). Do NOT return 'Body Corporate', 'Owners Corporation', 'Common Property', or a strata-plan number as the company - those name the property/owners, NOT the client who engages you.>",
    "clientEmail": "<their email address>",
    "clientPhone": "<their phone number in any format>",
    "clientTitle": "<the client contact's job title / role if stated - e.g. 'Strata Manager', 'Body Corporate Manager', 'Property Manager', 'Portfolio Manager', 'Facilities Manager', 'Building Manager'. Read it from their email signature (the line under their name) or a label next to their name. null if not stated.>",
    "siteContactName": "<name of the on-site contact for access arrangements, if different from client>",
    "siteContactPhone": "<phone of site contact>",
    "address": "<full site/project street address with suburb, state, postcode>",
    "strataPlan": "<strata plan number e.g. SP12345, or lot/plan reference>",
    "orderNumber": "<work order number, PO number, Q.R. quote reference, or job number - JUST THE DIGITS/ALPHANUMERIC IDENTIFIER, NOT the label. Strip 'JOB', 'JOB #', 'JOB NO', 'REF', 'REF:', 'ORDER #', 'Q.R.', 'QR', '#' and similar prefixes. If the RFQ says 'JOB #744463' return just '744463'. If the RFQ says 'Quote Ref: ABC-1234' return just 'ABC-1234'. The downstream system prepends its own 'Q.R ' prefix, so any label text you include will appear twice (e.g. 'Q.R JOB #744463' instead of the correct 'Q.R 744463').>",
    "visitDate": "<any mentioned date - requested visit, deadline, quote-by date - in DD/MM/YYYY or Month YYYY format>",
    "projectDescription": "<1-2 sentence summary of what work is requested>",
    "requesters": [
        {
            "name": "<full name of a person who requested or manages this request>",
            "title": "<their title as printed, e.g. 'Strata Manager', 'Assistant Strata Manager', 'Body Corp Manager', or null>",
            "email": "<their email ONLY if printed in the document, otherwise null>",
            "phone": "<their phone ONLY if printed in the document, otherwise null>",
            "role": "<strata_manager | assistant | order_by | issued_by | contact>"
        }
    ],
    "instanceOwnerStaff": [
        {
            "name": "<full name of the instance-owner employee>",
            "email": "<email if visible, otherwise null>",
            "role": "<assessor | site_supervisor | estimator | unknown>"
        }
    ]
}

WHERE TO FIND EACH FIELD:

CLIENT CONTACT (the person/company who placed the order - you invoice them):
- clientName: Look in "From:", email signature, "Ordered By", "Requested By", "Regards", "Kind regards", "Sent by", any name at bottom of email, letter heading, or RFQ header
- clientCompany: Look in email signature, letterhead, "Company:", domain of email address (e.g. john@abcstrata.com.au → "ABC Strata"), "Strata Manager:", "Managing Agent:"
- clientEmail: Look in "From:", email headers, signature block, "Email:", "E:", contact details section
- clientPhone: Look in email signature, "Ph:", "Phone:", "Tel:", "Mobile:", "M:", "T:", contact section, any phone number near the client name
- clientTitle: Look in the email signature (the role line under their name, e.g. "Strata Manager | ABC Strata"), a "Position:", "Title:", "Role:" label, or a role printed next to their name on the form (e.g. "Strata Manager: Jane Smith"). Leave null if no role/title is stated.

SITE CONTACT (person for physical site access - you call them to arrange visits):
- siteContactName: Look for "Site Contact:", "Access Contact:", "Building Manager:", "Caretaker:", "On-site:", "Contact for access:", "Resident manager:"
- siteContactPhone: Phone number listed next to or near the site contact name

AUSTRALIAN STRATA QUOTATION REQUEST FORMS (very common - read the field labels carefully):
- "Bill To" line, letterhead, or company logo = the strata MANAGING AGENT company that engages and pays you. Extract this as clientCompany (e.g. "GK Strata Management"). This IS the client. It is usually printed at the top of the form or as a logo, even if the "Bill To:" label itself looks blank in the text layer - read the letterhead/logo.
- "Body Corp Manager", "Strata Manager", "Order By", or the person who signed off ("Kind regards, <name>") = the CLIENT CONTACT. Extract as clientName.
- "Property Affected: Common Property" means the work is on the building's common property. "Common Property", "Body Corporate", and "Owners Corporation" are NOT the client and NOT a company name - NEVER put them in clientName or clientCompany. The body corporate / owners corporation is the property's owner group, identified by the strata plan number, not the client who engaged you.
- "Contractor Name" / "Contractor and Address" = the business RECEIVING the request (this is the instance owner / quoting builder). Route to instanceOwnerStaff[] or ignore; it is NEVER the client.
- "Site Contact(s)" (often one per unit, e.g. "Margaret-Anne (U4)", "Emma (U9)") = people to call for physical access. Extract the first into siteContactName/siteContactPhone.

ORDER / REFERENCE NUMBER (this is HIGH PRIORITY - Australian strata RFQs almost always have one):
- Common labels: "Work Order", "Work Order No", "WO", "W.O.", "Order No", "Order #", "PO", "P.O.", "Purchase Order", "Reference", "Ref", "Ref:", "Reference No", "Job No", "Job Number", "Quote No", "Quote Reference", "Quote Ref", "Q.R.", "QR"
- "Q.R." stands for "Quote Reference" - this is RJ Bird's quote prefix; if you see something like "Q.R. 12345" or "QR12345", extract it WITH the prefix
- The number is often near the top of the document, in the header, or in an email subject line like "Re: WO 4567" or "Subject: Q.R. 8901"
- If multiple references exist, extract the FIRST one labeled as a work order or quote reference (not invoice numbers)
- Examples of valid extractions: "WO 4567", "PO-12345", "Q.R. 8901", "Job 2026-042", "Ref: ABC/2026/123"

SITE ADDRESS (this is HIGH PRIORITY - extract the FULL address with suburb + state + postcode):
- Look for labels: "Site:", "Site Address:", "Property:", "Property Address:", "Address:", "Location:", "At:", "Re:", "Subject:", "Premises:"
- The address may also appear without a label, directly in the body text (e.g. "Please quote for repairs at 12 Smith Street, Bondi NSW 2026")
- Australian address format: [unit/]<number> <street name> <street type>, <suburb> <STATE> <postcode>
- Examples: "12 Smith Street, Bondi NSW 2026", "Unit 5/42 Wattle Crescent, Newport NSW 2106", "5-7 Marine Parade, Manly QLD 4179"
- If the address is split across multiple lines, combine them into one comma-separated string
- If only a partial address is given (e.g. just suburb), extract what you can find
- DO NOT confuse the BILLING/CLIENT address (in the email signature) with the SITE address (where the work is done) - the SITE address is where remediation will happen

STRATA PLAN NUMBER (this is HIGH PRIORITY - most Australian strata RFQs include it):
- Common formats: "SP12345", "SP 12345", "Strata Plan 12345", "Strata Plan: 12345", "S.P. 12345", "SP No 12345"
- Lot/Plan references: "Lot 5 SP12345", "Lot 3 in SP9876"
- May appear next to the address: "12 Smith St, Bondi NSW 2026 (SP12345)"
- May appear in subject line or header: "Re: SP12345 - Balcony Repairs"
- If you see ANY of these patterns, extract it (preserve "SP" prefix; the system will normalize it)
- If only a lot number is mentioned without a strata plan, leave strataPlan as null (lots are not strata plans)

IMPORTANT: If the document is an email, the sender IS the client. Extract their name and company from the "From:" header or email signature. Do not leave clientName/clientCompany as null if there is ANY name or company mentioned.

DOUBLE-CHECK before responding: Did you find an address? A strata plan number? An order/quote reference? If the document looks like an Australian strata RFQ and you returned null for these, scan the document AGAIN - they're almost certainly there.

Document (\${documentLabel}):
\${content}

Return ONLY the raw JSON object.`;

export const ASSESSOR_EXTRACTION_PROMPT = `Extract ONLY the inspector information from these site assessment notes.

CRITICAL: You MUST return ONLY valid JSON - no markdown, no code blocks, no explanations, just the raw JSON object. Use null for any field not found.

Return this exact JSON structure:
{
    "assessorName": "<name of the person who wrote these notes or conducted the site visit, or null>",
    "visitDate": "<date of the site visit/inspection, or null>"
}

WHAT TO LOOK FOR:
- assessorName: Look for "Assessed by", "Inspection by", "Site visit by", "Report by", "Notes by", signatures, or any name that appears to be the author/inspector.
- visitDate: Look for "Date", "Site visit date", "Inspection date", "Attended on", or any date mentioned.
  * If a specific date is found, return in DD/MM/YYYY format (e.g., "15/01/2026")
  * If only month/year is mentioned, return in "Month YYYY" format (e.g., "January 2026")

DO NOT extract client details, addresses, or contact information from inspector notes - these are in the RFQ document.

Inspector Notes (\${documentLabel}):
\${content}

Remember: Return ONLY the raw JSON object with assessorName and visitDate fields.`;

export const OCR_DOCUMENT_PROMPT = `You are analysing a photo of a document for text extraction (OCR).

This may be:
- A Request for Quote (RFQ) document
- A quote, proposal, or invoice
- Engineer or inspection report
- Site assessment notes (handwritten or typed)
- Any other business document with text

TASK: Extract ALL readable text from this image. Preserve the structure as much as possible.

For business documents, pay special attention to:
- Customer/Client names and company
- Contact details (email, phone, address)
- Job/Quote/Order numbers
- Site addresses
- Job descriptions or scope of work
- Any dates, deadlines, or reference numbers

Return ONLY the extracted text content. If multiple sections exist, separate them with line breaks.
If no significant text is found, return "NO_TEXT_FOUND".`;

// Stage 1 image import (2026-10-13). One call sorts up to 12 small images so
// only the ones that carry writing go on to OCR_DOCUMENT_PROMPT. Consumer:
// src/services/stage1-image-text-triage.ts (parseImageTextTriage). Anything it
// cannot parse is treated as a photo, so a bad answer never loses a photo.
export const IMAGE_TEXT_TRIAGE_PROMPT = `You are sorting images a builder imported for a building repair quote.
There are {IMAGE_COUNT} images, given in order. The first image is Image 1, the next is Image 2, and so on.

For each image choose ONE kind:
- "document": mainly a page, sheet or screen of writing. For example a request for quote, an email, a letter, a form, a report page, an invoice, handwritten notes, or a screenshot of text.
- "both": a site photo (a building, room, defect, material or equipment) that ALSO shows meaningful writing worth reading. For example measurements written on a wall, a compliance plate, a product label or a sign.
- "photo": a site photo with no meaningful writing. Watermarks, timestamps and tiny incidental text do not count.

Return ONLY JSON in this exact shape, with one entry for every image:
{"images":[{"index":1,"kind":"photo"}]}`;

export const RFQ_SUMMARY_PROMPT = `Analyse this building/construction Request for Quote (RFQ) or email and provide a concise, actionable summary for a builder. Use Australian English spelling throughout (analyse, organise, specialise, colour, behaviour, labour, etc.).

EVIDENCE-INVENTORY RULE (BINDING): this call may receive only the trigger RFQ text even though other project reference files are already attached. The <project_reference_inventory> block is the deterministic authority for which companion documents are attached. Never infer that a report, drawing, video, photo, email or attachment is missing merely because the RFQ says it is attached, will be forwarded separately, or does not reproduce its contents. If a matching document appears in the inventory, describe it as supplied/available. If the inventory cannot confirm it, say only that the RFQ refers to it and its project-level availability has not yet been verified; do not state that it was not supplied.

Return a plain text summary in this EXACT format:

REQUEST SUMMARY
• [1-2 sentence summary of what work is being requested]

SCOPE OF WORK
• [bullet points of specific work items, areas, or trades mentioned]

KEY REQUIREMENTS
• [any deadlines, access requirements, compliance needs, insurance, special conditions]

NOTES
• [anything unclear, missing, or that needs clarification from the client]

If the document is vague or doesn't clearly specify work, note what's missing under NOTES.
If it's a simple email with minimal detail, keep the analysis brief and flag what needs to be confirmed.

<project_reference_inventory>
\${referenceInventory}
</project_reference_inventory>

Document (\${documentLabel}):
\${content}`;

// ============================================================================
// PROJECT_SUMMARY_PROMPT
// ----------------------------------------------------------------------------
// Pre-analysis "best best best guess" pass. Runs after Stage 1 (Data Entry)
// when the user clicks Next, BEFORE the heavy Risk Analysis pass. Produces a
// structured JSON summary the user reviews on the Project Summary page.
//
// Critical contract: The model MUST commit to a guess. It MUST NOT refuse.
// It MUST always write a scope_objective even when the user provided nothing.
// It MUST be honest about confidence - low confidence is fine, missing data
// is fine, but the model never abstains.
// ============================================================================
export const PROJECT_SUMMARY_PROMPT = `You are the intake AI for an Australian building maintenance and remediation business. Your job is to read EVERYTHING we know about a new construction project - the RFQ documents, site photos, inspector notes, contact details, every extracted field - and produce a Project Summary that the builder will review BEFORE we run the full risk analysis.

This is a sanity-check step. The builder needs to know what you think the job is so they can confirm before we commit to a full quote.

{LOCALITY_BLOCK}

The locality block above has already been resolved deterministically from the project address and business profile. Use its state, regulatory body, and market rate language throughout your summary. Do NOT invent or override the locality - just consume it.

# CRITICAL RULES

1. **You MUST commit to a best guess about job intent and classification.** Never refuse, never say "insufficient information". This does NOT authorise an unverified physical cause: commit to what the client wants priced, while keeping causal uncertainty explicit.
2. **You MUST always write a scope_objective.** Even when the user provided nothing - even when documents are sparse - write exactly ONE sentence based on whatever signals you have. Open with "The objective of this quote is to provide a price", then "to" before a verb phrase ("to repair the roof membrane") or "for" before a noun phrase ("for crack repairs"), name the concrete physical work and within-property location, and end exactly at "at the above property." Both forms are house style. Write "a price", singular, even when there are several works. Mark its source clearly so the builder knows it's inferred.
2a. **MIRROR THE CLIENT'S WORDING.** When the RFQ, email, or inspector notes contain a direct job description, echo that language in your scope_objective. If the RFQ says "rebuild the brickworks in the bin area", your scope_objective should start with "to rebuild the brick wall in the bin area" - NOT generic paraphrasing like "provide pricing for brickwork repairs". Echo the client's verbs ("rebuild", "repair", "replace", "install") and their noun phrases. Do not add filler. Do not sanitise into generic trade language. The client should read your objective and immediately recognise their own request.
2b. **END AT "AT THE ABOVE PROPERTY."** Refer to the property with that exact ending, not the actual street address and not "at the site". The address already appears in the quote header, so a phrase like "at 15 Smith Street, Bondi" is redundant. Correct: "The objective of this quote is to provide a price to rebuild the damaged section of brick wall in the bin area at the above property."
2c. **NAME THE PHYSICAL WORK, NOT THE AI'S STRATEGY.** Never use an internal planning/diagnostic label such as "evidence-backed", "cause-first", "causal rectification", "chosen/selected/recommended approach", or generic "remediation works" as the scope_objective. Translate it into the concrete requested repair and exact work area. Keep named consultants and drawing identifiers in supporting description/inclusion fields; where relevant the objective may say only "in accordance with the referenced engineering design".
3. **Honesty about confidence is more valuable than false certainty.** Low confidence (40-60%) is perfectly acceptable. Missing data is acceptable. Refusing to guess is NOT acceptable.
4. **Use Australian construction context.** Use the locality block's market rate language and regulator name. Use AUD. Reference common Australian Standards (AS 4654.2 waterproofing, AS 3740 wet area, AS 3500.3 stormwater, etc.) when relevant to the project type.
5. **Source attribution matters.** For each key fact, cite where you got it from (which document, which photo, which note field).
5a. **PRE-DIAGNOSIS EPISTEMIC BOUNDARY.** Stage 1.5 happens before diagnosis. Observations and measurements may be factual. Reported history must be attributed. A causal explanation is a hypothesis unless an attached RFQ, engineer's report, or specialist report explicitly adopts that conclusion. For investigative or hybrid work use language such as "may be contributing" and "requires diagnostic confirmation"; never turn confidence into evidence or write an unqualified "caused by", "resulting from", "root cause is", or "site investigation identified [cause]". Defect Description must state the symptom and observed conditions, not assert the cause. best_guess_summary must separate observed conditions from possible contributors.
6. **★ USE THE DEEP CONTEXT ★** The CONTEXT block contains a <deep_context> section with:
   - <this_clients_history>: previous projects for the same client/company
   - <this_strata_plan_history>: previous quotes at the same strata plan
   You MUST mine this deep context for insights:
   - If the client has history, mention recurring patterns ("This client typically requests...")
   - If the strata plan has history, note ongoing maintenance themes
   Prior work may calibrate category, value range and RJ Bird voice only. It cannot establish a current-project fact or cause. The scope_objective must stay anchored to the current RFQ and evidence.

# WORK-TYPE CLASSIFICATION

Classify this project into ONE work-type category for \`project_type\`, choosing the single best-matching SLUG from the WORK-TYPE CATEGORIES list below. Match on the ACTUAL work involved, not a loose theme: a timber or aluminium WINDOW or DOOR REPLACEMENT is a window/joinery category (e.g. window_replacement_internal), it is NOT a facade job. Set \`project_type_label\` to the chosen category's human label. Use "general" ONLY if the job genuinely matches none of the categories.

{CATEGORY_TAXONOMY}

# OUTPUT FORMAT

Return STRICT JSON with this EXACT shape (no markdown, no commentary, just the JSON object):

{
  "project_type": "<the single best-matching work-type SLUG from the WORK-TYPE CATEGORIES list above (e.g. 'window_replacement_internal'); use 'general' only if none fit>",
  "project_type_label": "<the human label for the chosen category, taken from the list (e.g. 'Window Replacement (Internal / Unit)'); use 'General' for general>",
  "project_type_confidence": <0-100>,
  "project_type_reasoning": "<1-2 sentence explanation of why this category>",

  "locality_resolved": {
    "jurisdiction": "<echo the jurisdiction code from the locality block (e.g. AU-NSW) - do not override>",
    "state": "<echo the state (e.g. NSW) - do not override>",
    "regulator": "<echo the regulatoryBody from the locality block>",
    "confidence": <0-100 integer; echo the locality block's resolution confidence>
  },

  "rfq_mode": "prescriptive" | "investigative" | "hybrid",
  "rfq_mode_confidence": <0-100>,
  "rfq_mode_reasoning": "<1-2 sentence explanation. 'prescriptive' = client has defined exact scope (e.g. 'rebuild 4m² of brick wall', 'replace 3 window panes') and just wants a price. 'investigative' = client describes a symptom (e.g. 'balcony leaking', 'cracks appearing') and wants the builder to diagnose root cause before pricing. 'hybrid' = mix of defined tasks plus open-ended diagnosis.>",

  "scope_objective": "<EXACTLY ONE sentence opening 'The objective of this quote is to provide a price', then 'to' before a verb phrase ('to rebuild the brick wall') or 'for' before a noun phrase ('for crack repairs'), followed by the concrete physical work and exact work area, ending exactly at 'at the above property.' ALWAYS WRITE THIS. MIRROR the client's own wording from the RFQ/notes wherever possible (e.g. if they wrote 'rebuild the brickworks in the bin area', echo that phrasing - do NOT paraphrase to 'provide pricing for bin area repairs'). Do not write the actual street address or an internal strategy/diagnostic label. Good example: 'The objective of this quote is to provide a price to rebuild the damaged section of brick wall in the bin area at the above property.'>",
  "scope_objective_source": "user_provided" | "extracted_from_rfq" | "inferred_from_photos" | "best_guess",
  "scope_objective_confidence": <0-100>,

  "estimated_value_range": { "low": <number>, "high": <number>, "currency": "AUD" },
  "estimated_value_confidence": <0-100>,
  "estimated_value_reasoning": "<1 sentence explanation of how you arrived at this range>",

  "complexity_factors": ["<factor 1>", "<factor 2>", ...],
  "urgency_signal": "emergency" | "urgent" | "normal" | "flexible",

  "key_facts": [
    { "label": "Address", "value": "<value or 'unknown'>", "source": "<where you found it>", "confidence": <0-100> },
    { "label": "Client", "value": "<the managing agent / client company that engaged us, plus the client contact person if known - e.g. 'GK Strata Management (Megan Pollock)'. NOT 'Body Corporate' or 'Common Property' - those name the property/owners, not the client.>", "source": "<source>", "confidence": <0-100> },
    { "label": "Building Type", "value": "<value>", "source": "<source>", "confidence": <0-100> },
    { "label": "Defect Description", "value": "<value>", "source": "<source>", "confidence": <0-100> },
    { "label": "Site Access", "value": "<value>", "source": "<source>", "confidence": <0-100> }
  ],

  "requote_signal": {
    "is_requote": <true | false>,
    "confidence": <0-100>,
    "previous_quote_doc_number": <the number N from the "--- Document N ---" header of the previous quote PDF if one is present in <rfq_documents>, else null>,
    "change_request_summary": "<1-2 sentence summary of what the client is asking to be modified vs the previous quote (e.g. 'Remove item 4 (gutter cleaning) and add a new line for downpipe replacement; reduce scope to Unit 7 only.'). Empty string if not a re-quote.>",
    "reasoning": "<1-2 sentences explaining what signals you used to make the call.>"
  },

  "data_quality": {
    "score": <0-100>,
    "what_we_have": ["<thing 1>", "<thing 2>", ...],
    "what_we_lack": ["<thing 1>", "<thing 2>", ...],
    "ai_confidence_overall": <0-100>
  },

  "missing_information": [
    "<specific piece of info that would help>"
  ],

  "suggested_clarifying_questions": [
    "<question the builder could ask the client to fill in gaps>"
  ],

  "best_guess_summary": "<3-4 sentence narrative description of what we believe this job is. Include specifics like project type, approximate location/building, value band, urgency, and what photos/docs reveal. Written for the builder to read and either confirm or correct.>"
}

# CONFIDENCE CALIBRATION

- 90-100: Stated explicitly in the documents
- 75-89: Strongly implied by multiple sources
- 60-74: Implied by one source or visible in photos
- 40-59: Educated guess based on patterns
- 0-39: Pure inference, very low signal

# COMPLEXITY FACTORS to look for

height work, live site occupied, asbestos suspected, strata access required, after hours, multi-trade coordination, structural engineer needed, council approval needed, crane access needed, weather sensitive, body corporate approval, insurance claim work, urgent emergency response, partial demolition required

# RFQ MODE CLASSIFICATION

Classify the client's intent into one of three modes. This drives whether the downstream diagnostic pipeline should investigate root causes or just price the stated tasks:

- **prescriptive**: the client has defined the exact scope and just wants a price. Examples: "rebuild 4m² of brick wall in bin area", "replace 3 window panes in unit 3A", "repaint the front fence in colour X". Characteristic signal: explicit quantities, specific tasks, no symptom language.
- **investigative**: the client describes symptoms and expects the builder to diagnose and propose remediation. Examples: "balcony leaking", "cracks appearing in wall", "bathroom smells damp", "paint flaking off". Characteristic signal: problem descriptions without stated fixes.
- **hybrid**: both modes present in one RFQ. Examples: "replace the damaged awning AND investigate the cause of the water staining on the wall below it". Characteristic signal: mix of defined work and open-ended diagnosis requests.

When in doubt, prefer 'investigative' - it's safer to diagnose before pricing than to price a symptom-only fix.

# URGENCY SIGNALS to look for

- emergency: words like "ASAP", "leak", "active water", "safety hazard", "today"
- urgent: "this week", "before christmas", deadlines mentioned
- normal: standard quote request, no time pressure
- flexible: "when you have time", "no rush"

# RE-QUOTE DETECTION (v5.38.0)

A "re-quote" is when the client is asking you to update / revise / re-issue an existing quote that was previously sent — not investigate a fresh job. Re-quotes can skip the full Risk Analysis + Work Method pipeline because the underlying work was already diagnosed last time; the builder just needs to adjust the scope and re-price.

**Set requote_signal.is_requote = true ONLY when BOTH of these are true:**

1. One of the documents in <rfq_documents> is itself a quote PDF that RJ Bird (or similar builder) previously issued — look for headers like "QUOTATION", "QUOTE NUMBER", "QR.XXXX", an itemised scope table with prices, "Subject: Quote for ...", or formatting consistent with a final quote document. Set previous_quote_doc_number to that document's number from its "--- Document N ---" header.
2. The RFQ email, inspector notes, or covering message explicitly asks for a modification/update/revision of that quote — e.g. "please update quote Q12345 to include …", "could you re-issue with item 4 removed", "the body corporate has approved this with the following changes", "please revise scope to add …", "please remove the gutter line and re-send".

If only condition 1 is true (a quote is attached but the request is for a fresh job at the same site), set is_requote = false — that's a new project that happens to have historical context.

If only condition 2 is true (the request mentions a prior quote but the PDF isn't attached), set is_requote = false and note in reasoning that the previous quote is missing.

**Confidence calibration for is_requote:**
- 90-100: Both signals strongly present; previous quote PDF identified; change request explicit.
- 70-89: Both signals present but change request is vague ("please update as discussed").
- 50-69: Soft signal — e.g. an email reply chain referencing a prior quote with implicit edits.
- Below 50: Treat as NOT a re-quote.

When in doubt, prefer is_requote = false. The downstream pipeline (Risk Analysis → WMS → Scope Builder) is safe; the re-quote shortcut bypasses it only when we're highly confident.

# CONTEXT TO ANALYSE

{CONTEXT}

# REMEMBER

- Output the JSON object only. No markdown fences. No commentary before or after.
- Always write a scope_objective even with sparse data.
- Use AUD for value ranges.
- Be honest about confidence - better to say 45% than to refuse.
- Cite sources for key facts.`;

// ============================================================================
// REQUOTE_SCOPE_EXTRACTION_PROMPT (v5.38.0)
// ----------------------------------------------------------------------------
// Parse a previously-issued quote PDF (already OCR'd at import; we receive the
// raw text) into structured scope items + apply the client's change-request
// edits, all in one AI pass. Output is loaded directly into Stage 4 (Scope
// Builder) and the user reviews + refines from there.
//
// Triggered only when Project Summary's requote_signal.is_requote === true
// AND the user clicks "Skip to Scope Builder" on the Stage 1.5 review page.
// The normal Stage 2 (Risk Analysis) + Stage 3 (WMS) are bypassed because
// they were already done when the original quote was issued.
//
// Pricing: per Peter's directive (2026-05-21), prices CARRY OVER from the
// previous quote and Stage 4 surfaces a stale-pricing banner. Edits: the AI
// applies the client's change-request automatically; modified/added/removed
// items are tagged in requoteOrigin so the UI can show chips and the user
// can revert if needed.
//
// {BUSINESS_IDENTITY} and {BUSINESS_QUOTE_PDF} name the signed-in business
// (F9). RJ Bird reads "RJ Bird Building Pty Ltd, Customer Zero" and "RJ Bird
// quote PDF", as this prompt always did. Filled by extractRequoteScope.
// ============================================================================
export const REQUOTE_SCOPE_EXTRACTION_PROMPT = `You are processing a re-quote request for an Australian building maintenance / remediation business ({BUSINESS_IDENTITY}). The client previously received a quote and is now asking for an updated version with specific changes. Your job: parse the previous-quote text into structured scope items, then apply the client's change-request to produce the NEW scope.

Use Australian English spelling throughout (analyse, specialise, colour, behaviour, centre, metre).

# INPUTS

<previous_quote_format>{PREVIOUS_QUOTE_FILENAME}</previous_quote_format>

<previous_quote_text>
{PREVIOUS_QUOTE_TEXT}
</previous_quote_text>

<change_request_summary>
{CHANGE_REQUEST_SUMMARY}
</change_request_summary>

<additional_notes>
{ADDITIONAL_NOTES}
</additional_notes>

# YOUR TASK

Step 1 — PARSE: Extract every scope line item from the previous-quote text. A scope item is a discrete priced line (e.g. "Waterproofing membrane to balcony 14m² ......... $4,500"). Look for:
- Numbered or bulleted line items in the scope/works table
- Each item's title (short, e.g. "Waterproofing membrane"), description (longer text explaining the work), and amount (the price in AUD)
- Skip line items that are totals, subtotals, GST lines, payment-terms, or boilerplate

Step 2 — APPLY CHANGES: Read the change_request_summary carefully. For each instruction:
- "remove item X" / "delete the gutter line" / "no longer want X" → mark the matching item as removed (requoteOrigin = "removed")
- "add line for X" / "include X" / "also want X" → create a NEW item with requoteOrigin = "added". Estimate a price if the request implies one (e.g. "add 6m of gutter at $80/m"); otherwise set amount = 0 and add a note that pricing needs review.
- "change X to Y" / "increase X" / "reduce scope to" → modify the matching item, set requoteOrigin = "modified". Keep the original title/description/amount fields populated with the NEW values; record the prior values in requoteOriginalValues.
- Lines not mentioned by the change request → carry over verbatim with requoteOrigin = "carried_over".

If a change-request instruction is ambiguous or can't be matched to a specific line, do NOT guess wildly — emit an entry in unresolved_change_instructions with the instruction text + your interpretation attempt.

# OUTPUT FORMAT

Return STRICT JSON with this EXACT shape (no markdown fences, no commentary):

{
  "extraction_notes": "<1-2 sentence narrative of how cleanly the previous quote parsed (e.g. 'Clean parse: 6 items extracted from a well-formatted {BUSINESS_QUOTE_PDF}. Change request applied without ambiguity.')>",

  "items": [
    {
      "title": "<short title, e.g. 'Waterproofing membrane to balcony'>",
      "description": "<longer description of the work, preserving the client-facing language from the previous quote>",
      "amount": <number — the price in AUD, GST treatment per the source quote; 0 if added without a price>,
      "amountConfidence": <0-100 — how confident you are in this number; 100 if read directly from the previous quote, 40-60 if estimated for an added item>,
      "requoteOrigin": "carried_over" | "modified" | "added" | "removed",
      "requoteOriginalValues": {
        "title": "<original title before modification, or null>",
        "description": "<original description before modification, or null>",
        "amount": <original amount before modification, or null>
      },
      "requoteNote": "<optional short note explaining this line's status — e.g. 'Price unchanged; quote >6 months old, may need refresh', 'Added per change request: please add 6m downpipe at $80/m', 'Removed per change request: no longer want gutter cleaning'>"
    }
  ],

  "unresolved_change_instructions": [
    "<change-request instruction text that you could not confidently match to a scope line>"
  ],

  "previous_quote_date": "<the date PRINTED on the previous quote, as ISO yyyy-mm-dd (e.g. '2026-05-23'), or null if no date is shown. Report the date EXACTLY as printed. Do NOT work out how old the quote is — the application calculates the age from this date.>",

  "stale_pricing_warning": <true | false — set true ONLY when the previous quote itself carries an explicit price-review / validity-expired indicator (e.g. it literally says prices are subject to review). Do NOT set this based on how old the quote looks — the application decides age-staleness from previous_quote_date. When unsure, false.>,

  "stale_pricing_reason": "<if stale_pricing_warning is true, a 1-sentence reason based on what the QUOTE SAYS (not its age), else empty string. NEVER state how many days or months old the quote is — the application writes the age sentence itself.>"
}

# RULES

- Carry the prices over verbatim from the previous quote. The user has explicitly chosen this path (re-quote shortcut) and Stage 4 will surface a stale-pricing banner so they can refresh as needed.
- Items marked "removed" still appear in the output array (so the user can see + revert); the consumer will filter them out of the active scope while preserving them as removed-history.
- Items marked "added" should get a sensible title/description even if the price is uncertain. Set amount = 0 + amountConfidence < 50 in that case so the UI knows to highlight the gap.
- An added item's title follows "<Location> - <Work>" (location first, the work as nouns, e.g. "Unit 4 Balcony - Balustrade Resealing") and its description is one sentence naming the work as a noun ("Resealing of the balustrade base plates to the Unit 4 balcony."), per the house scope-writing contract. No "we", no benefit or reassurance wording. carried-over and modified lines keep the previous quote's wording.
- requoteOriginalValues: only populate when requoteOrigin === "modified". For other origins, leave the inner fields null.
- Output the JSON object only. No markdown, no commentary before or after.`;

export const KEY_MOMENTS_PROMPT = `You are analysing a site assessment video transcript to identify KEY MOMENTS worth capturing as photos. Use Australian English spelling throughout.

VIDEO DURATION: \${videoDuration} seconds
MAXIMUM FRAMES: \${maxFrames}

TRANSCRIPT WITH TIMESTAMPS:
\${transcript}

TASK: Identify the most important moments to capture as photos. Look for:
1. When the inspector describes a specific defect or issue
2. When pointing at or showing something important
3. When measurements or standards are mentioned
4. When safety hazards are noted
5. When different areas/rooms/locations are entered

Return ONLY a raw JSON array of timestamps:
[
    { "time": <seconds>, "label": "<brief description>", "context": "<what inspector was saying>" }
]

Return AT MOST \${maxFrames} frames. For short videos, fewer is better:only capture genuinely distinct moments.`;

export const BATCH_CLASSIFY_PROMPT = `You are classifying a batch of building/construction documents that were uploaded together for a quoting project.

For each document, determine its role:
- "rfq":The Request for Quote, tender, work order, or email requesting work. This is the PRIMARY document describing what the client wants done. There should be exactly ONE rfq.
- "scope_of_works":A pre-made Scope of Works / SOW document authored by the client, consultant, or engineer that itemises the required works (numbered sections, inclusions/exclusions, trade descriptions). This is an AUTHORITATIVE brief the builder should price against rather than invent. Decide it from the content, never from the file name. Distinct from an RFQ email: the RFQ is the request, the scope_of_works is the detailed work spec.
- "assessor_notes":A site inspection report or site inspection notes, assessment notes, or field observations written by the person who visited the site for this builder (inspector, assessor, or estimator on a site visit). Contains observations about conditions, defects, measurements.
- "report":Engineer's report, asbestos report, hazmat survey, compliance certificate, or another specialist or third-party technical report written by someone other than the builder's own site visitor.
- "competitor":A quote or proposal from another builder/contractor, used as reference.
- "plan":Site plan, floor plan, architectural drawing, or survey.
- "correspondence":Follow-up emails, meeting notes, or general communication that is NOT the primary RFQ.
- "other":Anything that doesn't fit the above categories.

RULES:
1. Exactly ONE document should be classified as "rfq":pick the one most likely to be the original request for work
2. If an email contains a work request or scope of works, it IS the rfq
3. A standalone formal scope document (numbered work items, inclusions/exclusions, issued by consultant/engineer) is "scope_of_works" even if an RFQ email also exists
4. Inspector notes focus on site CONDITIONS and OBSERVATIONS, not on requesting work
5. Reports from engineers, consultants, or specialists (e.g. asbestos, structural) are "report"
6. Return ONLY valid JSON array:no markdown, no code blocks

Documents to classify:
\${documentList}

Return this exact JSON structure:
[
  { "index": 0, "classification": "rfq", "confidence": 95, "reason": "Contains work order number and scope of works from strata manager" },
  { "index": 1, "classification": "assessor_notes", "confidence": 88, "reason": "Site visit observations noting defects and measurements" },
  { "index": 2, "classification": "report", "confidence": 92, "reason": "Asbestos survey report from licensed inspector" }
]`;

export const PHOTO_LABEL_PROMPT = `You are labelling construction site photos for a building maintenance/remediation project. Use Australian English spelling throughout (labelling, analysing, organising, specialising, colour, behaviour, centre, metre, etc.).

PROJECT CONTEXT:
\${projectContext}

For each photo, provide a short descriptive label (3-8 words) that describes what the photo likely shows, based on the project context and common construction photography patterns.

Guidance:
- Photos near the start often show site overview, access, building exterior
- Middle photos often show specific defects, damage areas, conditions
- Later photos often show details, measurements, close-ups of issues
- Use Australian construction terminology (e.g. "balcony membrane failure", "concrete spalling", "rising damp")

Photos to label (by position):
\${photoList}

Return ONLY a raw JSON array:
[
  { "index": 0, "label": "Site overview - building exterior" },
  { "index": 1, "label": "Concrete spalling on balcony soffit" }
]`;

export const PDF_COMPARISON_PROMPT = `You are the QuoteMaker AI analysing a FINAL SENT QUOTE PDF against what the AI originally generated. Your job is to identify every difference so the system can learn and improve.

PROJECT: \${address}
CLIENT: \${clientName}

AI ORIGINAL SCOPE (what the AI generated):
\${originalScope}

FINAL SENT QUOTE (extracted from the uploaded PDF):
\${pdfContent}

Compare the AI original against the final sent quote. Identify:
1. Items where the price changed (and by how much)
2. Items where the title or description was reworded
3. Items the user added that the AI didn't include
4. Items the AI included but the user removed
5. Inclusions that were added, removed, or reworded

Return ONLY valid JSON matching this schema (no markdown, no code fences):
{
  "summary": "<One sentence: how close was the AI to the final?>",
  "accuracy_pct": <0-100 integer>,
  "total_ai_original": <number>,
  "total_final_sent": <number>,
  "items_matched": <number of items that appear in both>,
  "items_added_by_user": <number>,
  "items_removed_by_user": <number>,
  "changes": [
    {
      "original_title": "<AI's title>",
      "final_title": "<what was in the sent PDF>",
      "original_amount": <number>,
      "final_amount": <number>,
      "final_description": "<the scope item description from the sent PDF, or null if unchanged>",
      "final_inclusions": ["<inclusion line from sent PDF>"],
      "change_type": "<matched|price_increased|price_reduced|reworded|modified>",
      "note": "<brief explanation of what changed and why it matters>"
    }
  ],
  "new_items": [
    {
      "title": "<item title from PDF not in AI original>",
      "description": "<scope item description from the PDF>",
      "inclusions": ["<inclusion line>"],
      "amount": <number or 0 if unclear>,
      "note": "<why this matters - AI should include this next time>"
    }
  ],
  "removed_items": [
    {
      "title": "<AI item not in final PDF>",
      "note": "<why it was likely removed>"
    }
  ],
  "learnings_suggested": [
    {
      "type": "<pricing|scope_template|business_rule|language|inclusions>",
      "category": "<waterproofing|concrete|roofing|facade|structural|painting|general|etc>",
      "title": "<short learning title>",
      "description": "<what the AI should do differently next time>"
    }
  ]
}

RULES:
- Match items by title similarity (they may be reworded slightly)
- Be specific with dollar amounts - don't round
- For learnings_suggested, focus on actionable improvements
- If the PDF is poorly extracted, do your best with what's available
- accuracy_pct should reflect how much editing the user had to do (100 = no changes needed)
- Return ONLY the JSON object`;

// {BUSINESS_QUOTE} says whose quote this is (F9): RJ Bird reads "RJ Bird
// Building quote", as this prompt always did. Filled by
// buildGoldStandardExtractionPrompt in services/gold-standard-extraction.js.
export const GOLD_STANDARD_EXTRACTION_PROMPT = `You are extracting a completed {BUSINESS_QUOTE} into gold-standard training data for QuoteMaker.

The quote PDF is the AUTHORITATIVE source for final output wording, scope structure, prices, validity, HBCF/admin presentation, and quote metadata. The RFQ / brief context is optional input-side evidence: use it only to understand what the builder was responding to. Do not let RFQ wording override the final sent quote.

SOURCE FILE FORMAT:
{SOURCE_FILE_NAME}

PROJECT CONTEXT (optional; quote PDF wins if it disagrees):
Project Type (user-suggested): {PROJECT_TYPE}
Address hint (optional): {ADDRESS}

RFQ / BRIEF CONTEXT (optional input-side evidence):
{RFQ_CONTEXT}

PDF CONTENT (final sent quote, authoritative):
{PDF_CONTENT}

Return ONLY valid JSON matching this structure. No markdown fences, no comments:
{
  "schemaVersion": "rj-bird-gold-standard-v2",
  "address": "<site/project address exactly as written on the quote; prefer SITE ADDRESS / prepared-for site line, never the builder office address>",
  "projectType": "<strata|commercial|residential|industrial|mixed_use|other>",
  "region": "<NSW|VIC|QLD|WA|SA|TAS|ACT|NT|null>",
  "year": <4-digit year from quote date, or null>,
  "quoteTitle": "<header title / literal offer, e.g. 'RBB/SSC (0426) - Unit 9' or quote subtitle>",
  "scopeObjective": "<exact objective paragraph if present (copy it verbatim; extraction is a faithful record of what was sent). If missing but clearly inferable from the quote objective area, reconstruct ONE sentence using the current RJ Bird PILLAR 0 formula: 'The objective of this quote is to provide a price to {verb phrase naming the work} at the above property.' or 'The objective of this quote is to provide a price for {noun phrase naming the work} at the above property.' End exactly at 'at the above property.' with no purpose tail.>",
  "quoteMeta": {
    "quoteNumber": "<Q.R / order / quote reference exactly as shown, or empty string>",
    "dateQuoted": "<date on quote, or empty string>",
    "validityDays": <60|90|null>,
    "validityText": "<validity text exactly as shown, or empty string>",
    "client": "<prepared-for client / strata plan / company, or empty string>",
    "assessor": "<assessor/estimator if visible, or empty string>"
  },
  "totals": {
    "subtotalExGst": <number, or null>,
    "gstAmount": <number, or null>,
    "totalIncGst": <number, or null>,
    "itemAmountsBasis": "inc_gst"
  },
  "totalValue": <number - subtotalExGst for backwards compatibility, or null>,
  "totalIncGst": <number - final client-facing total including GST, or null>,
  "scopeItems": [
    {
      "code": "<item number as shown>",
      "title": "<exact scope item title>",
      "description": "<full client-facing description/body for this item, preserving meaningful paragraph order but removing page headers/footers/filenames>",
      "description_brief": "<1-2 sentence client-facing summary if the quote has one; otherwise a faithful concise summary of the item, not generic reassurance>",
      "subheadings": [
        { "heading": "<subheading text>", "body": "<paragraph under it, or empty>", "bullets": ["<bullet text>"] }
      ],
      "inclusions": ["<explicit inclusion/deliverable bullets only; do not invent routine housekeeping>"],
      "notes": [
        { "kind": "<allowance|provisional|indicative|reference|exclusion_clarification|hbcf|validity|general>", "text": "<exact note/allowance/provisional text>", "params": { } }
      ],
      "amount": <number - client-facing item amount including GST if the quote item table is inc-GST>,
      "amountIncGst": <number or null>,
      "amountExGst": <number or null>,
      "gstAmount": <number or null>,
      "pricingBasis": "inc_gst",
      "category": "<waterproofing|concrete|roofing|facade|structural|painting|tiling|plumbing|electrical|carpentry|demolition|scaffolding|general|remediation>",
      "sourceEvidence": ["<quote page/section clue, e.g. 'Scope of Works item 1'>"]
    }
  ],
  "adminItems": [
    {
      "kind": "<hbcf|administration|preliminaries|other>",
      "title": "<visible admin/HBCF title>",
      "description": "<visible admin/HBCF description>",
      "amount": <number including GST or GST-free premium as shown>,
      "amountIncGst": <number or null>,
      "amountExGst": <number or null>,
      "gstAmount": <number or null>,
      "notes": [{ "kind": "hbcf", "text": "<visible HBCF/admin wording>" }]
    }
  ],
  "exclusions": ["<explicit exclusions only>"],
  "pricingPattern": {
    "roundingObserved": "<nearest_10|whole_dollar|cents|unknown>",
    "hbcfShown": <true|false>,
    "hbcfRateObserved": <number|null>,
    "validityRuleObserved": "<60_days_under_20k|90_days_over_20k|other|unknown>",
    "priceNotes": ["<observable pricing/admin pattern, not guessed margins>"]
  },
  "rfqSignals": {
    "clientRequestedWorks": ["<work phrases from RFQ that are reflected in final quote>"],
    "builderAddedFromExperience": ["<scope/admin items present in quote but not explicit in RFQ>"],
    "rfqIgnoredOrDeferred": ["<RFQ signals not priced, only if clear>"]
  },
  "metadata": {
    "client": "<same as quoteMeta.client>",
    "assessor": "<same as quoteMeta.assessor>",
    "dateQuoted": "<same as quoteMeta.dateQuoted>",
    "quoteNumber": "<same as quoteMeta.quoteNumber>",
    "validityDays": <60|90|null>,
    "scopeObjective": "<same as top-level scopeObjective>"
  },
  "extractionQuality": {
    "confidence": <0.0_to_1.0>,
    "missingFields": ["<field names that were absent or uncertain>"],
    "warnings": ["<footer noise removed, totals ambiguous, scanned OCR uncertainty, etc.>"]
  }
}

EXTRACTION RULES:
- Extract exact visible wording. Do not paraphrase the final sent quote except for description_brief when the quote lacks a separate brief field.
- Remove repeated page headers, page footers, file names, image captions such as SITE PHOTOS, and builder office address noise from item descriptions.
- Do not infer materials/labour/equipment costBreakdown, unitRates, tradeHours, or margins from a client-facing PDF. If not explicitly shown, omit those fields.
- Keep HBCF / Home Building Compensation Fund / DBPA registration / administration lines in adminItems, not scopeItems. ScopeItems should be the client-facing works being delivered.
- Preserve allowances and provisional wording as typed notes with kind "allowance" or "provisional".
- Item amounts in RJ Bird final quote PDFs are client-facing inc-GST amounts unless the quote explicitly labels otherwise. Populate amount and amountIncGst with that visible item amount.
- totalValue remains subtotalExGst only for backwards compatibility. Always also populate totalIncGst.
- Validity is deterministic business signal: under $20k usually 60 days, over $20k usually 90 days. Extract what is shown; do not correct the quote.
- If totals do not reconcile exactly because of OCR or PDF formatting, extract the visible numbers and add a warning in extractionQuality.warnings.
- Return only the JSON object.`;

// ============================================================================
// QM ASSISTANT SYSTEM PROMPT v2 LOADER (Ship 4, Wave 2)
// ============================================================================
// The v2 assistant system prompt lives in a standalone .md file so designers
// and Peter can edit it without touching JavaScript. This module exposes:
//
//   ASSISTANT_SYSTEM_PROMPT_PATH  - repo-relative path to the .md (Vite fetch)
//   loadAssistantSystemPrompt()   - async loader, wired to Ship 3's agent loop
//   ASSISTANT_LEGACY_PROMPT_MARKER - used by the kill-switch path
//
// Kill switch: `localStorage.qm_disable_assistant_prompt_v2 === 'true'` makes
// `loadAssistantSystemPrompt()` resolve to the legacy inline prompt (the large
// `buildPrompt()` string in QuoteMakerAssistantPanel.jsx) by returning the
// sentinel below; the caller (Ship 3) checks for this sentinel and falls back
// to the existing `buildPrompt` path.
//
// We intentionally do NOT import the legacy inline prompt here - it lives in
// the panel component because it needs live `formData`/`stage` closures. Ship
// 3 handles the branch.
// ============================================================================

export const ASSISTANT_SYSTEM_PROMPT_PATH = 'src/config/prompts/assistant-system.md';
export const ASSISTANT_LEGACY_PROMPT_MARKER = '__QM_ASSISTANT_USE_LEGACY_PROMPT__';

// In-memory cache so we don't re-fetch the .md every turn. Invalidated by
// a page reload or by calling `loadAssistantSystemPrompt({ fresh: true })`.
let _assistantSystemPromptCache = null;

/**
 * Load the v2 system prompt. Works in both browser (fetch + ?url import) and
 * Node test environments (dynamic fs import). Returns the marker string if the
 * kill switch is active so Ship 3 can branch to the legacy path without this
 * module knowing anything about React state.
 *
 * @param {{ fresh?: boolean }} [options]
 * @returns {Promise<string>} the raw markdown body (placeholders intact)
 */
export async function loadAssistantSystemPrompt({ fresh = false } = {}) {
    // Kill switch - respect both localStorage and the window flag for parity
    // with every other Wave-2 Ship.
    try {
        if (typeof localStorage !== 'undefined' && localStorage.getItem('qm_disable_assistant_prompt_v2') === 'true') {
            return ASSISTANT_LEGACY_PROMPT_MARKER;
        }
        if (typeof window !== 'undefined' && window.qm_disable_assistant_prompt_v2) {
            return ASSISTANT_LEGACY_PROMPT_MARKER;
        }
    } catch { /* SSR or restricted context - fall through and load v2 */ }

    if (!fresh && _assistantSystemPromptCache) {
        return _assistantSystemPromptCache;
    }

    // Browser path: pull the markdown as a Vite ?raw asset. Ship 3's agent
    // loop awaits this at turn-time, so a one-shot dynamic import is fine.
    if (typeof window !== 'undefined') {
        try {
            // Vite inlines the markdown as a string when suffixed with ?raw.
            // Using a static specifier (not a computed URL) keeps HMR happy
            // and avoids `@vite-ignore`.
            const mod = await import('./prompts/assistant-system.md?raw');
            const text = String(mod?.default ?? mod ?? '');
            if (!text) throw new Error('empty markdown');
            _assistantSystemPromptCache = text;
            return text;
        } catch (viteErr) {
            // Fallback: fetch the source file directly from the dev server.
            try {
                const resp = await fetch('/src/config/prompts/assistant-system.md');
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const text = await resp.text();
                _assistantSystemPromptCache = text;
                return text;
            } catch (fetchErr) {
                console.warn('[QM Assistant] Failed to load system prompt v2, falling back to legacy.', { viteErr: viteErr?.message, fetchErr: fetchErr?.message });
                return ASSISTANT_LEGACY_PROMPT_MARKER;
            }
        }
    }

    // Node / test path: read from disk via fs.
    try {
        const fs = await import('node:fs/promises');
        const path = await import('node:path');
        const url = await import('node:url');
        // Resolve relative to this source file so the test runner works from
        // any cwd.
        const here = path.dirname(url.fileURLToPath(import.meta.url));
        const mdPath = path.join(here, 'prompts', 'assistant-system.md');
        const text = await fs.readFile(mdPath, 'utf8');
        _assistantSystemPromptCache = text;
        return text;
    } catch (nodeErr) {
        console.warn('[QM Assistant] Node loader failed for system prompt v2:', nodeErr?.message);
        return ASSISTANT_LEGACY_PROMPT_MARKER;
    }
}

/**
 * Test-only helper: clear the in-memory cache so back-to-back test runs see
 * a fresh load. Not re-exported via the default export - treat as internal.
 */
export function __resetAssistantSystemPromptCache() {
    _assistantSystemPromptCache = null;
}
